/**
 * «Запостить в тестовый канал» — диалог (docs/release-2026-09-19.md §9.1): сводка, удержание
 * 800 мс, ход отправки и ссылка на пост в тестовом канале.
 *
 * ЧТО ОН ОБЕЩАЕТ И ЧЕГО НЕ ДЕЛАЕТ. Пост уходит в ТЕСТОВЫЙ канал бренда ровно в том виде, как
 * выйдет в боевой: то же подписанное содержание, тот же способ отправки, картинка и файл. Версия
 * при этом не меняется — ни статус, ни слот, ни кнопка «Утвердить…»: тестовая отправка — не
 * публикация, и ключ её подписан только на тестовый канал (`preview`, код 3).
 *
 * УДЕРЖАНИЕ, А НЕ КЛИК. Это то же действие «пост уходит в Telegram», что и одобрение, и
 * промах мышью здесь — пост в канале, пусть и тестовом. Поэтому единственное, что отправляет,
 * — `HoldToConfirm` (800 мс, отпустил раньше — отмены достаточно, клавиатура держит так же);
 * «Повторить» после отказа — тоже удержание.
 *
 * ХОД — ОТ ПОЛОСЫ. Диалог только показывает слежение (`useTestSendTracker` живёт в
 * `DecisionPanel`): закрытый посреди отправки, он не теряет её, а мета-строка полосы доводит
 * её до «открыть» сама.
 *
 * ОБЪЯСНЕНИЯ — В ⓘ (правило партии §1): в теле — канал, форма поста, ход и исход.
 */

import { nowMs } from '@smm/contracts';
import { getFirstUrl } from '@smm/contracts/preview';
import { postLabel } from '@smm/contracts/schemas';
import type { JSX } from 'react';
import type { RenderModel, TestSend } from '../../api/types.ts';
import { fileSize, momentMsk } from '../../components/format.ts';
import { InfoPopover } from '../../components/InfoPopover.tsx';
import { IconCheck, IconExternal, IconWarn } from '../../components/icons.tsx';
import { Modal } from '../../components/Modal.tsx';
import { visualViewOf } from '../visual/visualState.ts';
import { shortHash } from './ApproveDialog.tsx';
import { channelUrl } from './FailedPanel.tsx';
import { HoldToConfirm } from './HoldToConfirm.tsx';
import styles from './TestSendDialog.module.css';
import {
  deviated,
  deviationText,
  isStalled,
  isTerminal,
  outcomeText,
  TEST_SEND_STATE_WORD,
  type TestSendTracker,
} from './testSend.ts';

export interface TestSendDialogProps {
  readonly model: RenderModel;
  readonly tracker: TestSendTracker;
  /** Обязан жить всю жизнь владельца: `Modal` держит фокус эффектом от него. */
  readonly onClose: () => void;
}

/** Ссылка на пост в тестовом канале: из строки отправки, иначе — собранная из id. */
export function testSendLink(t: TestSend): string | null {
  if (t.link !== null) return t.link;
  return t.telegram_message_id === null ? null : channelUrl(t.chat_id, t.telegram_message_id);
}

type StepState = 'done' | 'current' | 'pending';

/** Три шага хода: ключ → публикатор забрал → пост в канале. */
function stepsOf(t: TestSend): readonly (readonly [string, StepState])[] {
  const taken: StepState = t.state === 'signed' ? 'current' : 'done';
  const posted: StepState =
    t.state === 'sent' ? 'done' : t.state === 'leased' ? 'current' : 'pending';
  return [
    ['Ключ подписан — только на тестовый канал', 'done'],
    ['Публикатор забрал отправку', taken],
    ['Пост в тестовом канале', posted],
  ];
}

export function TestSendDialog({ model, tracker, onClose }: TestSendDialogProps): JSX.Element {
  const { tracked, latest, posting, error, send } = tracker;
  const canonical = model.canonical;
  const document = canonical.document ?? null;
  // Id тестового канала на экране появляется с первой отправкой этой версии: до неё его не
  // отдаёт ни один маршрут интерфейса (канал — поле конфига бренда, выбирает его только
  // @smm/brand-config).
  const chatId = latest?.chat_id ?? null;
  const now = nowMs();

  // Карточка — тем же правилом, что полоса карточки превью (`getFirstUrl` по сохранённому):
  // у обычного сообщения её может не быть вовсе (отключена в версии, ссылки нет), и «карточка
  // снизу» по одному методу была бы неправдой о том, что сейчас уйдёт в канал.
  const card = getFirstUrl(canonical);
  const form: readonly string[] = [
    canonical.method === 'sendRichMessage'
      ? 'расширенное сообщение, карточки не будет'
      : card.kind === 'card'
        ? `обычное сообщение, карточка снизу — ${card.host}`
        : 'обычное сообщение, без карточки',
    ...(canonical.media === null ? [] : ['картинка первым блоком']),
    ...(document === null
      ? []
      : [`файл «${document.file_name}» · ${fileSize(document.bytes)} последним блоком`]),
  ];
  // Кандидат визуала рисуется в пузыре превью по умолчанию, но в подписанное содержание не
  // входит, и отправляется версия без него. Кто жмёт «в тестовый канал», чтобы увидеть пост с
  // картинкой, обязан узнать это до удержания (ревью, ui-3).
  const proposalLeftOut = visualViewOf(model).state === 'proposed';

  const summary = (
    <div className={styles['summary']}>
      <div className={styles['summaryHead']}>
        <span className="eyebrow">Что уйдёт</span>
        <InfoPopover label="Что делает отправка в тестовый канал">
          <span>
            Пост уходит в тестовый канал бренда в том виде, как выйдет в боевой: тот же текст,
            разметка, картинка, файл и способ отправки. Так проверяют, как пост выглядит в настоящем
            клиенте Telegram.
          </span>
          <span>
            Версия не меняется: её статус, слот и утверждение остаются как есть. После проверки её
            же можно утвердить в боевой канал.
          </span>
          <span>
            Ключ этой отправки подписан только на тестовый канал: в боевой по нему ничего уйти не
            может.
          </span>
          <span>Кнопку нужно удерживать 800 мс: отпустите раньше — отправки не будет.</span>
        </InfoPopover>
      </div>
      <span className="meta" data-testid="test-send-channel">
        {chatId === null ? 'тестовый канал бренда' : `тестовый канал · ${chatId}`}
      </span>
      <span className="meta">в том виде, как выйдет в боевой</span>
      <span className="meta">{form.join(' · ')}</span>
      {proposalLeftOut ? (
        <span className="meta" data-testid="test-send-proposal">
          предложенная картинка в эту отправку не входит
        </span>
      ) : null}
      <span className="meta">
        {`${postLabel(model.post_no)} · v${String(model.version_no)} · sha256 ${shortHash(model.content_hash)}`}
      </span>
    </div>
  );

  const errorLine =
    error === null ? null : (
      <div className="val val-err" role="alert">
        <span>{error}</span>
      </div>
    );

  const closeButton = (label: string): JSX.Element => (
    <button type="button" className="btn btn-ghost btn-w" onClick={onClose}>
      {label}
    </button>
  );

  // ── ход: ключ выдан, публикатор ещё не отчитался ─────────────────────────────
  if (tracked !== null && !isTerminal(tracked.state)) {
    // Ключ превью живёт ровно 300 с: не забранная за это время отправка уже не уйдёт, и
    // «ждём публикатора» дальше было бы обещанием, которого никто не исполнит. Выданная и не
    // отчитанная дольше уборки закроется сервером как неудачная — так и сказано.
    const stalled = isStalled(tracked, now);
    return (
      <Modal
        title="В тестовый канал"
        subtitle={`${postLabel(model.post_no)} · v${String(model.version_no)}`}
        onClose={onClose}
      >
        {summary}
        <ol className={styles['steps']} aria-label="Ход отправки">
          {stepsOf(tracked).map(([label, state]) => (
            <li
              key={label}
              className={styles['step']}
              data-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className={styles['mark']} aria-hidden="true">
                {state === 'done' ? <IconCheck width={12} height={12} /> : null}
              </span>
              <span>{label}</span>
            </li>
          ))}
        </ol>
        <span className="meta" data-testid="test-send-state">
          {TEST_SEND_STATE_WORD[tracked.state]}
        </span>
        {stalled ? (
          <div className="val val-warn" role="alert">
            <span>
              {tracked.state === 'signed'
                ? 'Публикатор не забрал отправку за 5 минут — ключ истёк, и она уже не уйдёт. Проверьте, что сервис публикатора работает.'
                : 'Публикатор забрал отправку, но не отчитался дольше 10 минут — сервер закроет её как неудачную. Проверьте тестовый канал и сервис публикатора.'}
            </span>
          </div>
        ) : null}
        {errorLine}
        {/* «Свернуть», а не «Отмена»: отправку не отменить, она идёт дальше, и мета-строка
            полосы доведёт её до «открыть» сама. */}
        {closeButton('Свернуть')}
      </Modal>
    );
  }

  // ── исход этой отправки ──────────────────────────────────────────────────────
  if (tracked !== null) {
    const link = testSendLink(tracked);
    const when = momentMsk(tracked.finished_at ?? tracked.created_at, now);
    return (
      <Modal
        title="В тестовый канал"
        subtitle={`${postLabel(model.post_no)} · v${String(model.version_no)}`}
        onClose={onClose}
      >
        {summary}
        {tracked.state === 'sent' ? (
          <>
            {deviated(tracked) ? (
              <div className="val val-warn" data-testid="test-send-result">
                <IconWarn />
                <span>{`Отправлено · ${when}, но ${deviationText(tracked)}. Проверьте пост в канале: в боевом он выглядел бы так же.`}</span>
              </div>
            ) : (
              <div className="val val-info" data-testid="test-send-result">
                <IconCheck />
                <span>{`Отправлено в тестовый канал · ${when}`}</span>
              </div>
            )}
            {link === null ? null : (
              <a
                className="btn btn-pri btn-w"
                href={link}
                target="_blank"
                rel="noreferrer noopener"
                style={{ textDecoration: 'none' }}
              >
                <IconExternal /> Открыть в канале
              </a>
            )}
            {errorLine}
            {closeButton('Готово')}
          </>
        ) : (
          <>
            <div className="val val-err" data-testid="test-send-result">
              <span>{`${tracked.state === 'refused' ? 'Публикатор отказал' : 'Не вышло'} · ${when}: ${outcomeText(tracked)}`}</span>
            </div>
            {errorLine}
            <HoldToConfirm label="Повторить" disabled={posting} onConfirm={send} />
            {posting ? <span className="meta">Подписываем отправку…</span> : null}
            {closeButton('Отмена')}
          </>
        )}
      </Modal>
    );
  }

  // ── удержание: ничего не отправлено ──────────────────────────────────────────
  const previous = latest;
  return (
    <Modal
      title="В тестовый канал"
      subtitle={`${postLabel(model.post_no)} · v${String(model.version_no)}`}
      onClose={onClose}
    >
      {summary}
      {previous === null ? null : <PreviousSend send={previous} now={now} />}
      {errorLine}
      <HoldToConfirm label="Отправить в тестовый канал" disabled={posting} onConfirm={send} />
      {posting ? <span className="meta">Подписываем отправку…</span> : null}
      {closeButton('Отмена')}
    </Modal>
  );
}

/** Прошлая отправка этой версии — одной строкой: когда, чем кончилась, где посмотреть. */
function PreviousSend({ send, now }: { send: TestSend; now: number }): JSX.Element {
  const when = momentMsk(send.finished_at ?? send.created_at, now);
  const link = send.state === 'sent' ? testSendLink(send) : null;
  const word =
    send.state === 'sent'
      ? deviated(send)
        ? `отправлено, но ${deviationText(send)}`
        : 'отправлено'
      : isTerminal(send.state)
        ? `${TEST_SEND_STATE_WORD[send.state]}: ${outcomeText(send)}`
        : TEST_SEND_STATE_WORD[send.state];
  return (
    <span className="meta" data-testid="test-send-previous">
      {`Прошлая отправка ${when} — ${word}`}
      {link === null ? null : (
        <>
          {' · '}
          <a href={link} target="_blank" rel="noreferrer noopener">
            открыть
          </a>
        </>
      )}
    </span>
  );
}
